import { useLayoutEffect, useRef, useState } from 'react';
import { formatNumber } from '../lib/utils';
import { COUNTER_LOCALE_ATTR, COUNTER_MODE_ATTR, COUNTER_VALUE_ATTR } from '../lib/counter-contract';
import type { Locale } from '@worldcoo/monolith/core';

/**
 * Animated number counter using rAF with time-based exponential decay, driven imperatively.
 *
 * WHY IMPERATIVE, and not per-frame state. These counters animate during page load, competing with
 * hydration, image decode and the page's own queries — on the mid-range Android and older iPhones that
 * make up the traffic. Six counters at 60fps would be ~36 render commits in the worst possible window,
 * so the rAF loop writes `textContent` directly and React re-renders exactly ONCE per animation, when
 * the value settles. The formatter is cached, a DOM write is skipped when the formatted string has not
 * changed, and the loop exits as soon as the formatted output equals the target.
 *
 * WHAT REACT RENDERS is `settledText`: the value the counter last came to rest on. At rest React's
 * output and the DOM agree, so an unrelated re-render can never clobber the counter — only the
 * intermediate frames live outside React's knowledge, and only while the animation is running.
 *
 * HYDRATION. The first render must be reproducible on the server, and the two modes differ:
 *
 *   - Entrance effect (the default): the counter is *designed* to start at zero and count up, so both
 *     sides render zero. Deterministic, no suppression needed, and a genuine hydration bug on this node
 *     still reports. The count-up starts on mount, i.e. after React has finished comparing.
 *   - `instant`: there is no count-up, so rendering zero would ship the wrong figure in the HTML and
 *     shift layout when it corrected. This mode renders the real value and marks the node
 *     `suppressHydrationWarning` — React's documented mechanism for content that legitimately differs
 *     between server and client, which a live counter always can. Suppression makes React skip the
 *     patch, so the mount branch repairs the text when the two genuinely disagree. Scoped to this mode
 *     deliberately: every other counter keeps full mismatch reporting.
 *
 * The bug behind TEC-3689 / TEC-3695 was the campaign sidebar rendering a live figure on the first pass
 * WITHOUT declaring it, so a client that resolved a different aggregate than the server had written
 * mismatched on exactly that text node. Callers pass the real value and pick a mode; they must never
 * re-implement the guard themselves.
 *
 * `prefers-reduced-motion` skips the count-up: a correctness fix, and one less rAF loop.
 */

export function AnimateNumber({
	locale,
	value,
	suffix,
	testId,
	onAnimateComplete,
	decimalDigits = 0,
	className,
	instant = false,
}: {
	locale: Locale;
	value: number;
	suffix?: string;
	testId?: string;
	onAnimateComplete?: () => void;
	decimalDigits?: number;
	className?: string;
	/** Skip the count-up and render the real figure on the first pass. See the note above. */
	instant?: boolean;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const rafRef = useRef(0);
	const isFirstRef = useRef(true);
	const onCompleteRef = useRef(onAnimateComplete);
	onCompleteRef.current = onAnimateComplete;

	const target = value ?? 0;

	const fmt = (n: number) => `${formatNumber(locale, n, decimalDigits)}${suffix ? ` ${suffix}` : ''}`;
	const fmtRef = useRef(fmt);
	fmtRef.current = fmt;

	// The first paint: the real figure for `instant`, zero for the entrance effect. Both are values the
	// server reproduces exactly, which is what makes hydration safe in each mode.
	const [settledText, setSettledText] = useState(() => fmt(instant ? target : 0));
	const currentRef = useRef(instant ? target : 0);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return undefined;

		const targetText = fmtRef.current(target);
		const isFirst = isFirstRef.current;
		isFirstRef.current = false;

		if (isFirst) {
			currentRef.current = instant ? target : 0;

			if (instant) {
				// `suppressHydrationWarning` made React skip the text patch, so the DOM may still hold the
				// SERVER's figure. Repair only when the two genuinely differ, which keeps the ordinary mount
				// path free of DOM writes.
				if (el.textContent !== targetText) el.textContent = targetText;
				return undefined;
			}

			// Entrance effect: hydration is done, so counting up from zero is now safe. Nothing to animate
			// when the target is zero too.
			if (target === 0) return undefined;
		}

		// Three cases settle immediately instead of running the loop.
		//
		// `instant`: the caller does not want a count-up.
		//
		// Reduced motion: the visitor asked for no animation. Honouring it also skips the loop entirely.
		//
		// Hidden document: `requestAnimationFrame` does not fire while the document is hidden, so in a
		// background tab — a link opened with middle-click or cmd-click, which is ordinary traffic — the
		// loop below would never advance past its first frame. The element would sit on the PRE-animation
		// text, showing the old figure while `data-value` already carries the new one, until the visitor
		// focuses the tab. There is no animation to be seen in a hidden tab anyway.
		//
		// All three still report completion, exactly as the "already at target" exit inside the loop does:
		// the bump coordination in `portal-animate-number` waits on that callback, and never receiving it
		// would leave the shared release counter hanging.
		const prefersReducedMotion =
			typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		const isDocumentHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

		if (instant || prefersReducedMotion || isDocumentHidden) {
			currentRef.current = target;
			if (el.textContent !== targetText) el.textContent = targetText;
			setSettledText(targetText);
			onCompleteRef.current?.();
			return undefined;
		}

		// Show the current value immediately (before the first rAF) to prevent a flash.
		el.textContent = fmtRef.current(currentRef.current);

		let prev = 0;

		const tick = (now: number) => {
			// Use rAF timestamps exclusively — performance.now() and rAF timestamps can diverge, making dt
			// negative on the first tick.
			const dt = prev ? (now - prev) / 1000 : 0;
			prev = now;

			// Time-based exponential decay (speed 8 ≈ settles in ~0.6s)
			currentRef.current += (target - currentRef.current) * (1 - Math.exp(-8 * dt));

			const text = fmtRef.current(currentRef.current);

			// Stop as soon as the formatted output matches the target — avoids unnecessary rAF ticks where
			// only the float changes but the visible text is already the final value.
			if (text === targetText) {
				currentRef.current = target;
				el.textContent = targetText;
				// The one re-render per animation: React's output now matches the DOM again.
				setSettledText(targetText);
				onCompleteRef.current?.();
				return;
			}

			// Skip the DOM write when the formatted text has not changed.
			if (el.textContent !== text) {
				el.textContent = text;
			}

			rafRef.current = requestAnimationFrame(tick);
		};
		rafRef.current = requestAnimationFrame(tick);

		return () => cancelAnimationFrame(rafRef.current);
		// The formatting inputs are deps too: without them a locale or precision change re-renders the
		// stale `settledText` until the next value change. A re-run settles immediately (the value is
		// already at target), firing `onAnimateComplete` exactly as a target change does.
	}, [target, instant, locale, decimalDigits, suffix]);

	return (
		<div
			ref={ref}
			className={`inline-block tracking-tight tabular-nums${className ? ` ${className}` : ''}`}
			data-testid={testId}
			// Read back by the client-error reporter's counter snapshot; the shared constants are the
			// contract (see `counter-contract.ts`). The mode matters there: without it an `entrance` node
			// that has not hydrated yet still shows its zero first paint against a real value attribute,
			// which reads as a mismatch when it is simply React not having taken the node over.
			{...{
				[COUNTER_VALUE_ATTR]: value,
				[COUNTER_MODE_ATTR]: instant ? 'instant' : 'entrance',
				[COUNTER_LOCALE_ATTR]: locale,
			}}
			// Only `instant` renders a live figure on the first pass, so only it can legitimately differ
			// from the server. The entrance effect renders zero on both sides and keeps full reporting.
			suppressHydrationWarning={instant}
		>
			{settledText}
		</div>
	);
}
